import { Component, OnInit, Inject, OnDestroy } from "@angular/core";
import { Subject, timer } from "rxjs";
import { environment } from "../../../../../environments/environment";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { BuyChips } from "../../../../shared/models";
import { CurrencyMultiplierPipe } from "../../../../shared/utils/currency-multiplier.pipe";
import { ManagerService } from "../../../../core/services/manager.service";
import { takeUntil } from "rxjs/operators";
import { CurrencyPipe } from "../../../../shared/utils/currency.pipe";
import { CurrencyDeviderPipe } from "../../../../shared/utils/currency-devider.pipe";
import { ConfigService } from "../../../../core/services/config.service";

@Component({
  selector: "app-buy-chips",
  templateUrl: "./buy-chips.component.html",
  styleUrls: ["./buy-chips.component.scss"],
})
export class BuyChipsComponent implements OnInit, OnDestroy {
  environment = environment;
  dialogTimeout = 30;
  value: number;
  minBlind: number;
  destroy$ = new Subject<boolean>();
  minAmount: number;
  config;
  constructor(
    public dialogRef: MatDialogRef<BuyChipsComponent>,
    @Inject(MAT_DIALOG_DATA) public buyChips: BuyChips,
    public managerService: ManagerService,

    private configService: ConfigService,
  ) {
    this.config = this.configService.config;
    this.minBlind = CurrencyDeviderPipe.prototype.transform(
      buyChips.step,
      buyChips.currency,
    );
    this.dialogTimeout = buyChips.dialogTimeout;
    this.buyChips.value = CurrencyDeviderPipe.prototype.transform(
      this.buyChips.value,
      this.buyChips.currency,
    );
    this.minAmount = CurrencyDeviderPipe.prototype.transform(
      buyChips.min,
      buyChips.currency,
    );
  }

  ngOnInit() {
    timer(0, 1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.dialogTimeout--;
        if (this.dialogTimeout <= 0) {
          this.dialogRef.close();
        }
      });

    // ** On Logout Close
    this.managerService.settings$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (settings) => {
        if (!settings) {
          this.dialogRef.close();
        }
      },
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  isEditing = false;

  enableEdit(): void {
    this.isEditing = true;
    setTimeout(() => {
      const input = document.querySelector<HTMLInputElement>("input");
      input?.focus();
      input?.select();
    });
  }

  disableEdit(): void {
    this.isEditing = false;
  }

  get cashAvailableDisplay(): number {
    if (
      this.buyChips.cashAvailable !== null &&
      this.buyChips.cashAvailable !== undefined
    ) {
      return this.buyChips.cashAvailable;
    }
    return this.buyChips.available;
  }

  getMaxDisplayAmount(): number {
    return CurrencyDeviderPipe.prototype.transform(
      this.getMaxChips(),
      this.buyChips.currency,
    );
  }

  /** newDesign: empty / non-numeric input */
  isBuyInInputEmptyOrInvalid(): boolean {
    const v = this.buyChips?.value;
    if (v === null || v === undefined) {
      return true;
    }
    const str = String(v).trim();
    if (str === "") {
      return true;
    }
    const n = Number(v);
    return Number.isNaN(n);
  }

  /** newDesign: 'empty' | 'range' | null (valid) */
  getBuyInValidationError(): "empty" | "range" | null {
    if (!this.config?.newDesign) {
      return null;
    }
    if (this.isBuyInInputEmptyOrInvalid()) {
      return "empty";
    }
    const n = Number(this.buyChips.value);
    if (n < this.minAmount || n > this.getMaxDisplayAmount()) {
      return "range";
    }
    return null;
  }

  getBuyInRangeTranslateParams(): { min: string; max: string } {
    const nb = this.buyChips.currency.NbDecimalVisible ?? 2;
    const fmt = (x: number) => {
      const normalized = Number(Number(x).toFixed(nb));
      return normalized.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: nb,
      });
    };
    return {
      min: fmt(this.minAmount),
      max: fmt(this.getMaxDisplayAmount()),
    };
  }

  clickOk(chipsValue: any) {
    if (this.config?.newDesign) {
      if (this.getBuyInValidationError() !== null) {
        return;
      }
    } else if (!chipsValue || chipsValue < this.minAmount) {
      return;
    }
    chipsValue = parseFloat(chipsValue);
    chipsValue = parseFloat(
      chipsValue.toFixed(this.buyChips.currency.NbDecimalVisible),
    );

    chipsValue = CurrencyMultiplierPipe.prototype.transform(
      chipsValue,
      this.buyChips.currency,
    );
    this.dialogRef.close(chipsValue);
  }

  clickCancel() {
    this.dialogRef.close(undefined);
  }

  clickChipsMin() {
    this.buyChips.value = CurrencyDeviderPipe.prototype.transform(
      this.buyChips.min,
      this.buyChips.currency,
    );
  }

  clickChipsMax() {
    this.buyChips.value = CurrencyDeviderPipe.prototype.transform(
      this.getMaxChips(),
      this.buyChips.currency,
    );
  }

  getMaxChips() {
    return this.buyChips.available > this.buyChips.max
      ? this.buyChips.max
      : this.buyChips.available;
  }

  // Increment or decrement current value by minBlind (step) and clamp to bounds
  incrementValue(direction: 1 | -1 = 1) {
    const step =
      this.minBlind ??
      CurrencyDeviderPipe.prototype.transform(
        this.buyChips.step,
        this.buyChips.currency,
      );

    const min = CurrencyDeviderPipe.prototype.transform(
      this.buyChips.min,
      this.buyChips.currency,
    );
    const max = CurrencyDeviderPipe.prototype.transform(
      this.getMaxChips(),
      this.buyChips.currency,
    );

    const current = Number(this.buyChips.value) || 0;
    let next = current + direction * step;
    if (next < min) next = min;
    if (next > max) next = max;
    this.buyChips.value = parseFloat(
      next.toFixed(this.buyChips.currency.NbDecimalVisible),
    );
    setTimeout(() => (document.activeElement as HTMLElement)?.blur());
  }
}
